/**
 * Arrondi et agrégation des moyennes — alignés sur le backend Laravel :
 * - `round($value, 2)` (PHP_ROUND_HALF_UP)
 * - `Deliberation::calculerMoyenneUE` / `calculerMoyenneGenerale`
 */

/** Équivalent PHP `round($value, 2)`. */
export function roundMoyenne(value: number): number {
	if (!Number.isFinite(value)) return 0
	return Number(Math.round((value + Number.EPSILON) * 100) / 100)
}

/** Affichage 2 décimales (point) après arrondi canonique. */
export function formatMoyenneDisplay(value: number | null | undefined): string {
	if (value === null || value === undefined || !Number.isFinite(Number(value))) return ''
	return roundMoyenne(Number(value)).toFixed(2)
}

/** Affichage note/moyenne avec fallback si absent (défaut « - »). */
export function formatNoteDisplay(value: number | null | undefined, fallback = '-'): string {
	if (value === null || value === undefined || !Number.isFinite(Number(value))) return fallback
	return formatMoyenneDisplay(Number(value))
}

export interface EcMoyenneInput {
	moyenne_ec: number | null | undefined
	coefficient?: number | null
}

/**
 * Moyenne UE pondérée par coefficient EC — même logique que `Deliberation::calculerMoyenneUE`.
 * Tous les EC fournis participent (y compris moyenne 0).
 */
export function computeMoyenneUeFromEcs(ecs: EcMoyenneInput[]): number {
	let totalMoyenne = 0
	let totalCoefficient = 0

	for (const ec of ecs) {
		if (ec.moyenne_ec === null || ec.moyenne_ec === undefined) continue
		const moyenneEc = Number(ec.moyenne_ec)
		if (Number.isNaN(moyenneEc)) continue
		const coefficient = Number(ec.coefficient ?? 1) || 1
		totalMoyenne += moyenneEc * coefficient
		totalCoefficient += coefficient
	}

	if (totalCoefficient === 0) return 0
	return roundMoyenne(totalMoyenne / totalCoefficient)
}

/**
 * Moyenne générale arithmétique des moyennes UE — même logique que `Deliberation::calculerMoyenneGenerale`.
 */
export function computeMoyenneGeneraleFromUes(
	ues: Array<{ moyenne_ue: number | null | undefined }>
): number {
	let totalMoyenne = 0
	let nombreUe = 0

	for (const ue of ues) {
		if (ue.moyenne_ue === null || ue.moyenne_ue === undefined) continue
		const moyenneUe = Number(ue.moyenne_ue)
		if (Number.isNaN(moyenneUe)) continue
		totalMoyenne += moyenneUe
		nombreUe++
	}

	if (nombreUe === 0) return 0
	return roundMoyenne(totalMoyenne / nombreUe)
}

/**
 * Mention / appréciation selon la moyenne — aligné sur
 * `NoteCalculationService::getMentionFromMoyenne` et l’ancien bulletin PHP.
 */
export function getMentionFromMoyenne(moyenne: number | null | undefined): string {
	if (moyenne === null || moyenne === undefined || !Number.isFinite(Number(moyenne))) {
		return ''
	}
	const m = Number(moyenne)
	if (m <= 5) return 'Médiocre'
	if (m < 10) return 'insuffisant'
	if (m < 12) return 'Passable'
	if (m < 14) return 'Assez Bien'
	if (m < 16) return 'Bien'
	if (m < 18) return 'Très Bien'
	return 'Excellent'
}
